/**
 * @file Renders the list of tier sub-investments that make up an investment.
 */
import keyBy from "lodash.keyby";
import { Space, Typography } from "antd";
import { useOutletContext } from "react-router-dom";

import { Value } from "@components/Text";

import type { SubInvestment } from "../../../helpers/types";
import type { Tier, OutletContext } from "../../types";

const LABEL_UNDEFINED = "Undefined";

type Props = {
  projectId: string;
  investments: SubInvestment[];
};

/**
 * Renders the tier name and amount for each sub-investment of an investment.
 */
const InvestmentInvestments = ({
  projectId,
  investments,
}: Props) => {
  const { getProject, getTierName } = useOutletContext<OutletContext>();

  const project = getProject(projectId);

  const { tiers } = project;
  const tiersMap: Record<string, Tier> = keyBy(tiers, "name");

  /**
   * Resolves the display name for a project tier, or a fallback when the tier is not found.
   */
  const getLabel = (projectTierId: string) => {
    const tier = tiersMap[projectTierId];

    if (tier) {
      return getTierName(tier);
    }

    return LABEL_UNDEFINED;
  };

  /**
   * Renders a single sub-investment's tier label and amount.
   */
  const renderInvestment = ({ projectTierId, amount }: SubInvestment, key: number) =>
    <Typography.Text key={key}>
      <Typography.Text type="secondary">
        {getLabel(projectTierId)}:
      </Typography.Text>
      {" "}
      <Value value={amount} />
    </Typography.Text>;

  return (
    <Space>
      {investments.map(renderInvestment)}
    </Space>
  );
};

export default InvestmentInvestments;
